/**
 * useNodeAddInteraction hook
 *
 * Listens for map clicks when node editing mode is active and adds new vertices
 * at the click position on the nearest line segment. The click must be on a
 * segment (not on an existing node) for a new node to be inserted.
 *
 * **Validates: Requirement 16.2** – WHEN a Registered_User clicks on a line
 * segment, THE Geometry_Editor SHALL add a new node at that location.
 */

import { useEffect, useCallback, useRef } from 'react';
import { toLonLat } from 'ol/proj';
import type OLMap from 'ol/Map';
import type MapBrowserEvent from 'ol/MapBrowserEvent';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  selectIsEditingGeometry,
  selectEditingFeatureId,
  selectEditingLayerId,
} from '../store/slices/geometryEditingSlice';
import { updateFeatureGeometryAsync } from '../store/slices/layersSlice';
import { addNode, type Geometry, type Coordinate } from '../services/GeometryEditor';
import { findNearestSegment, isClickOnExistingNode } from '../utils/findNearestSegment';
import { validateGeometry, type ValidatableGeometry } from '../utils/validateGeometry';

/**
 * Options for the useNodeAddInteraction hook.
 */
export interface UseNodeAddInteractionOptions {
  /** Live map value; preferred so restored editing binds after map initialization. */
  map?: OLMap | null;
  /** Backward-compatible map reference for existing consumers. */
  mapRef?: React.RefObject<OLMap | null>;
  /**
   * Tolerance in map coordinate units for detecting clicks on segments.
   * A click must be within this distance of a segment to trigger node addition.
   * Default: 0.0001 (approximately 11 meters at the equator in EPSG:4326)
   */
  segmentTolerance?: number;
  /**
   * Tolerance in map coordinate units for detecting clicks on existing nodes.
   * If a click is within this distance of an existing node, it is treated as
   * a node selection rather than a node addition.
   * Default: 0.00015
   */
  nodeTolerance?: number;
  /**
   * Optional callback invoked when geometry validation fails after a node add.
   * The edit is still committed but the user is warned about topology issues.
   */
  onValidationWarning?: (errors: string[]) => void;
}

/**
 * useNodeAddInteraction
 *
 * A React hook that handles adding new vertices to a geometry when the user
 * clicks on a line segment during node editing mode.
 *
 * Behavior:
 * - Only active when `isEditingGeometry` is true in Redux state
 * - Listens for single-click events on the map
 * - Determines if the click is on a line segment (not on an existing node)
 * - Finds the nearest segment to the click point
 * - Calculates the closest point on that segment to the click position
 * - Calls `addNode()` from the GeometryEditor service
 * - Dispatches the updated geometry to Redux via `updateFeatureGeometry`
 * - The node overlay automatically updates since it watches Redux state
 */
export function useNodeAddInteraction({
  map,
  mapRef,
  segmentTolerance = 0.0001,
  nodeTolerance = 0.00015,
  onValidationWarning,
}: UseNodeAddInteractionOptions): void {
  const dispatch = useAppDispatch();
  const isEditing = useAppSelector(selectIsEditingGeometry);
  const editingFeatureId = useAppSelector(selectEditingFeatureId);
  const editingLayerId = useAppSelector(selectEditingLayerId);
  const layers = useAppSelector((state) => state.layers.layers);

  // Use refs to avoid stale closures in the event handler
  const isEditingRef = useRef(isEditing);
  const editingFeatureIdRef = useRef(editingFeatureId);
  const editingLayerIdRef = useRef(editingLayerId);
  const layersRef = useRef(layers);
  const onValidationWarningRef = useRef(onValidationWarning);

  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);

  useEffect(() => {
    editingFeatureIdRef.current = editingFeatureId;
  }, [editingFeatureId]);

  useEffect(() => {
    editingLayerIdRef.current = editingLayerId;
  }, [editingLayerId]);

  useEffect(() => {
    layersRef.current = layers;
  }, [layers]);

  useEffect(() => {
    onValidationWarningRef.current = onValidationWarning;
  }, [onValidationWarning]);

  /**
   * Retrieves the geometry of the feature currently being edited.
   */
  const getEditingGeometry = useCallback((): Geometry | null => {
    const featureId = editingFeatureIdRef.current;
    const layerId = editingLayerIdRef.current;
    if (!featureId || !layerId) return null;

    const layer = layersRef.current.find((l) => l.id === layerId);
    if (!layer || !layer.features) return null;

    const feature = layer.features.find((f) => f.id === featureId);
    if (!feature || !feature.geometry) return null;

    return feature.geometry as unknown as Geometry;
  }, []);

  /**
   * Handles map click events for adding nodes.
   */
  const handleMapClick = useCallback(
    (event: MapBrowserEvent<UIEvent>) => {
      if (!isEditingRef.current) return;

      const featureId = editingFeatureIdRef.current;
      const layerId = editingLayerIdRef.current;
      if (!featureId || !layerId) return;

      const geometry = getEditingGeometry();
      if (!geometry) return;

      // Convert map pixel coordinate to geographic coordinate (lon/lat)
      const mapCoordinate = event.coordinate;
      const lonLat = toLonLat(mapCoordinate) as Coordinate;

      // Check if the click is on an existing node — if so, skip (let node selection handle it)
      if (isClickOnExistingNode(lonLat, geometry, nodeTolerance)) {
        return;
      }

      // Find the nearest segment to the click position
      const nearestSegment = findNearestSegment(geometry, lonLat, segmentTolerance);
      if (!nearestSegment) {
        // Click is not close enough to any segment
        return;
      }

      // Add the new node at the projected position on the segment
      const result = addNode(geometry, nearestSegment.segmentIndex, nearestSegment.projectedPoint);

      if (result.success && result.geometry) {
        // Validate the resulting geometry topology
        const validation = validateGeometry(result.geometry as unknown as ValidatableGeometry);
        if (!validation.valid && onValidationWarningRef.current) {
          onValidationWarningRef.current(validation.errors);
        }

        // Optimistically update Redux and persist; the thunk rolls back on failure.
        dispatch(
          updateFeatureGeometryAsync({
            layerId,
            featureId,
            geometry: result.geometry as unknown as GeoJSON.Geometry,
            previousGeometry: JSON.parse(JSON.stringify(geometry)) as GeoJSON.Geometry,
          })
        );
      }
    },
    [dispatch, getEditingGeometry, segmentTolerance, nodeTolerance]
  );

  /**
   * Register/unregister the click event listener on the map.
   */
  useEffect(() => {
    const liveMap = map ?? mapRef?.current ?? null;
    if (!liveMap) return;

    if (!isEditing) return;

    // Use 'singleclick' to avoid triggering on double-click zoom
    liveMap.on('singleclick', handleMapClick as any);

    return () => {
      liveMap.un('singleclick', handleMapClick as any);
    };
  }, [map, mapRef, isEditing, handleMapClick]);
}

export default useNodeAddInteraction;
